import RestService from 'shared/services/http-client';
import ServiceList from 'shared/services/http-client/list';
import { Contact, ContactsResponse, CreateContactPayload, UpdateContactPayload, ContactQueryParams, ContactType, ContactStatus } from 'shared/types';
import { mockContacts, generateMockContacts, mockUsers } from 'shared/helpers/mock-data';

export class ContactsService {
  /**
   * Get list of contacts with filtering and pagination
   * Maps to customer endpoints as per backend structure
   */
  static async getContacts(params: ContactQueryParams): Promise<ContactsResponse> {
    try {
      const response = await RestService.send({
        serviceName: ServiceList.getCustomers,
      });
      
      console.log('API Response received:', response.data);
      return response.data;
    } catch (error: any) {
      // For other errors, throw them up
      throw error;
    }
  }

  /**
   * Get a single contact by ID
   * Maps to GET /v1/customers/:id
   */
  static async getContact(id: number): Promise<Contact> {
    try {
      console.log(`Fetching contact ${id} from API: GET /v1/customers/${id}`);
      
      const response = await RestService.send({
        serviceName: ServiceList.getCustomer,
        params: { id: id.toString() }
      });
      
      console.log('Single contact API response:', response.data);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Create a new contact
   * Maps to POST /v1/customers
   */
  static async createContact(payload: CreateContactPayload): Promise<void> {
    try {
      console.log('Creating contact via API: POST /v1/customers', payload);
      
      await RestService.send({
        serviceName: ServiceList.createCustomer,
        body: payload
      });
      
      console.log('✅ Contact created successfully');
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update an existing contact
   * Maps to PUT /v1/customers/:id
   */
  static async updateContact(id: number, payload: UpdateContactPayload): Promise<void> {
    try {
      console.log(`Updating contact ${id} via API: PUT /v1/customers/${id}`, payload);
      
      await RestService.send({
        serviceName: ServiceList.updateCustomer,
        params: { id: id.toString() },
        body: payload
      });
      
      console.log(`✅ Contact ${id} updated successfully`);
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Delete a contact
   */
  static async deleteContact(id: number): Promise<void> {
    try {
      await RestService.send({
        serviceName: ServiceList.deleteCustomer,
        params: { id: id.toString() }
      });
    } catch (error: any) {
      if (error?.response?.status === 404) {
        console.warn('Delete contact API not available, simulating success');
        // Simulate successful deletion for development
        await new Promise(resolve => setTimeout(resolve, 500));
        return;
      }
      throw error;
    }
  }

  /**
   * Create an account for a contact
   */
  static async createAccount(contactId: number, accountData: { username: string; password: string }): Promise<void> {
    try {
      await RestService.send({
        serviceName: ServiceList.createAccount,
        params: { id: contactId.toString() },
        body: accountData
      });
    } catch (error: any) {
      if (error?.response?.status === 404) {
        console.warn('Create account API not available, simulating success');
        // Simulate successful account creation for development
        await new Promise(resolve => setTimeout(resolve, 500));
        return;
      }
      throw error;
    }
  }

  /**
   * Delete an account for a contact
   */
  static async deleteAccount(contactId: number): Promise<void> {
    try {
      await RestService.send({
        serviceName: ServiceList.deleteAccount,
        params: { id: contactId.toString() }
      });
    } catch (error: any) {
      if (error?.response?.status === 404) {
        console.warn('Delete account API not available, simulating success');
        // Simulate successful account deletion for development
        await new Promise(resolve => setTimeout(resolve, 500));
        return;
      }
      throw error;
    }
  }
}
